import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import {
  LogOut,
  Inbox,
  MessageSquare,
  MessageCircle,
  Clock,
  Send,
  Loader2,
  Phone,
  ScrollText,
  Crown,
  MapPin,
} from "lucide-react";
import { toast } from "sonner";
import { api, formattaData, formattaOra } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/site/Logo";
import { WhatsAppPanel } from "@/components/admin/WhatsAppPanel";
import { WhatsAppGira } from "@/components/admin/WhatsAppGira";

const STATI = ["In attesa", "In gestione", "Completata"];

const COLORE_STATO = {
  "In attesa": "border-amber-500/30 bg-amber-500/10 text-amber-400",
  "In gestione": "border-sky-500/30 bg-sky-500/10 text-sky-400",
  Completata: "border-emerald-500/30 bg-emerald-500/10 text-emerald-400",
};

const ETICHETTE_TAB = {
  richieste: "Richieste ricevute",
  chat: "Conversazioni attive",
  whatsapp: "WhatsApp",
  audit: "Registro attività",
  operatori: "Operatori in zona",
};

export default function AdminDashboard() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("richieste");
  const [stats, setStats] = useState(null);
  const [richieste, setRichieste] = useState([]);
  const [filtro, setFiltro] = useState("");
  const [conversazioni, setConversazioni] = useState([]);
  const [attiva, setAttiva] = useState(null);
  const [risposta, setRisposta] = useState("");
  const [audit, setAudit] = useState([]);
  const [posizioni, setPosizioni] = useState([]);
  const [condivisione, setCondivisione] = useState(false);
  const fondoRef = useRef(null);
  const watchRef = useRef(null);

  const isSuperadmin = user?.ruolo === "superadmin";

  const traccia = useCallback((azione, dettaglio = "") => {
    api.post("/admin/track", { azione, dettaglio }).catch(() => {});
  }, []);

  const cambiaTab = (t) => {
    setTab(t);
    traccia("Scheda aperta", ETICHETTE_TAB[t] || t);
  };

  const caricaRichieste = useCallback(async () => {
    const { data } = await api.get("/admin/richieste", { params: filtro ? { stato: filtro } : {} });
    setRichieste(data);
  }, [filtro]);

  const caricaChat = useCallback(async () => {
    const { data } = await api.get("/admin/chat/conversazioni");
    setConversazioni(data);
    setAttiva((prev) => (prev ? data.find((c) => c.id === prev.id) || prev : prev));
  }, []);

  const caricaStats = useCallback(async () => {
    const { data } = await api.get("/admin/stats");
    setStats(data);
  }, []);

  const caricaAudit = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/audit-log");
      setAudit(data);
    } catch {
      /* visibile solo al superadmin */
    }
  }, []);

  const caricaPosizioni = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/posizioni");
      setPosizioni(data);
    } catch {
      /* visibile solo al superadmin */
    }
  }, []);

  useEffect(() => {
    if (user !== undefined && user !== null) {
      caricaStats();
      caricaRichieste();
      caricaChat();
      if (user.ruolo === "superadmin") {
        caricaAudit();
        caricaPosizioni();
      }
      traccia("Dashboard aperta");
      const heartbeat = setInterval(() => traccia("Sessione attiva"), 120000);
      const t = setInterval(() => {
        caricaStats();
        caricaRichieste();
        caricaChat();
        if (user.ruolo === "superadmin") {
          caricaAudit();
          caricaPosizioni();
        }
      }, 6000);
      return () => {
        clearInterval(t);
        clearInterval(heartbeat);
      };
    }
  }, [user, caricaStats, caricaRichieste, caricaChat, caricaAudit, caricaPosizioni, traccia]);

  useEffect(() => {
    fondoRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [attiva]);

  useEffect(() => {
    return () => {
      if (watchRef.current !== null) navigator.geolocation.clearWatch(watchRef.current);
    };
  }, []);

  if (user === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-obsidian">
        <Loader2 className="animate-spin text-amber-400" size={32} />
      </div>
    );
  }
  if (user === null) return <Navigate to="/admin/login" replace />;

  const attivaCondivisione = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocalizzazione non supportata da questo browser");
      return;
    }
    watchRef.current = navigator.geolocation.watchPosition(
      async (pos) => {
        try {
          await api.post("/admin/posizione", {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
          });
        } catch {
          /* riprova al prossimo aggiornamento */
        }
      },
      () => {
        toast.error("Permesso posizione negato", {
          description: "Abilita la geolocalizzazione nel browser per condividere la posizione.",
        });
        setCondivisione(false);
      },
      { enableHighAccuracy: true, maximumAge: 60000 }
    );
    setCondivisione(true);
    toast.success("Condivisione posizione attiva", {
      description: "Il superadmin vede la tua posizione finché la condivisione è attiva.",
    });
  };

  const disattivaCondivisione = async () => {
    if (watchRef.current !== null) navigator.geolocation.clearWatch(watchRef.current);
    watchRef.current = null;
    try {
      await api.post("/admin/posizione/disattiva");
    } catch {
      /* la posizione resta ma risulta non attiva */
    }
    setCondivisione(false);
    toast.success("Condivisione posizione disattivata");
  };

  const esci = async () => {
    if (condivisione) await disattivaCondivisione();
    traccia("Uscita dalla dashboard");
    await api.post("/auth/logout");
    setUser(null);
    navigate("/admin/login");
  };

  const cambiaStato = async (id, stato) => {
    try {
      await api.patch(`/admin/richieste/${id}`, { stato });
      toast.success("Stato aggiornato");
      caricaRichieste();
      caricaStats();
      if (isSuperadmin) caricaAudit();
    } catch {
      toast.error("Aggiornamento non riuscito");
    }
  };

  const cambiaFiltro = (s) => {
    setFiltro(s);
    traccia("Filtro richieste", s || "Tutte");
  };

  const apriConversazione = (c) => {
    setAttiva(c);
    traccia("Chat letta", c.nome);
  };

  const rispondi = async (e) => {
    e.preventDefault();
    if (!risposta.trim() || !attiva) return;
    const testo = risposta;
    setRisposta("");
    await api.post(`/admin/chat/conversazioni/${attiva.id}/messaggi`, { testo });
    caricaChat();
    if (isSuperadmin) caricaAudit();
  };

  const toggleStatoChat = async () => {
    if (!attiva) return;
    const nuovo = attiva.stato === "Aperta" ? "Chiusa" : "Aperta";
    await api.patch(`/admin/chat/conversazioni/${attiva.id}`, { stato: nuovo });
    toast.success(nuovo === "Chiusa" ? "Conversazione chiusa" : "Conversazione riaperta");
    caricaChat();
    if (isSuperadmin) caricaAudit();
  };

  const tabCls = (t) =>
    `rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
      tab === t ? "bg-amber-500 text-obsidian" : "text-slate-300 hover:text-white"
    }`;

  return (
    <div className="min-h-screen bg-obsidian" data-testid="admin-dashboard">
      <header className="glass-panel sticky top-0 z-30 border-x-0 border-t-0">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 md:px-8">
          <Logo onClick={() => navigate("/")} />
          <div className="flex items-center gap-3">
            {isSuperadmin && (
              <span
                data-testid="admin-badge-superadmin"
                className="hidden items-center gap-1.5 rounded-full border border-amber-500/40 bg-amber-500/10 px-3 py-1 text-xs font-semibold text-amber-400 sm:inline-flex"
              >
                <Crown size={13} /> Superadmin
              </span>
            )}
            <button
              type="button"
              data-testid="admin-condividi-posizione-btn"
              onClick={condivisione ? disattivaCondivisione : attivaCondivisione}
              className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition-colors ${
                condivisione
                  ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-400"
                  : "border-slate-700 text-slate-300 hover:border-amber-500/50 hover:text-amber-400"
              }`}
            >
              <MapPin size={15} />
              <span className="hidden sm:inline">
                {condivisione ? "Posizione condivisa" : "Condividi posizione"}
              </span>
              {condivisione && (
                <span className="relative flex h-2 w-2">
                  <span className="absolute h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                  <span className="h-2 w-2 rounded-full bg-emerald-400" />
                </span>
              )}
            </button>
            <span className="hidden text-sm text-slate-400 md:block">{user.email}</span>
            <button
              type="button"
              data-testid="admin-logout-btn"
              onClick={esci}
              className="inline-flex items-center gap-2 rounded-full border border-slate-700 px-4 py-2 text-sm text-slate-300 transition-colors hover:border-red-500/50 hover:text-red-400"
            >
              <LogOut size={15} /> Esci
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-10 md:px-8">
        <h1 className="font-display text-2xl font-bold text-white sm:text-3xl">
          Dashboard operativa
        </h1>

        <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {[
            { label: "Richieste ricevute", val: stats?.richieste_totali, id: "admin-stat-richieste-totali" },
            { label: "In attesa", val: stats?.richieste_in_attesa, id: "admin-stat-in-attesa" },
            { label: "In gestione", val: stats?.richieste_in_gestione, id: "admin-stat-in-gestione" },
            { label: "Conversazioni attive", val: stats?.conversazioni_aperte, id: "admin-stat-conversazioni" },
          ].map((s) => (
            <div key={s.id} data-testid={s.id} className="rounded-2xl border border-slate-800 bg-slatecard p-5 text-center">
              <p className="font-display text-3xl font-extrabold text-amber-400">{s.val ?? "—"}</p>
              <p className="mt-1 text-xs text-slate-400 sm:text-sm">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap gap-2 border-b border-slate-800 pb-4">
          <button type="button" data-testid="admin-tab-richieste" onClick={() => cambiaTab("richieste")} className={tabCls("richieste")}>
            <span className="inline-flex items-center gap-2">
              <Inbox size={16} /> Richieste ricevute
            </span>
          </button>
          <button type="button" data-testid="admin-tab-conversazioni" onClick={() => cambiaTab("chat")} className={tabCls("chat")}>
            <span className="inline-flex items-center gap-2">
              <MessageSquare size={16} /> Conversazioni attive
            </span>
          </button>
          <button type="button" data-testid="admin-tab-whatsapp" onClick={() => cambiaTab("whatsapp")} className={tabCls("whatsapp")}>
            <span className="inline-flex items-center gap-2">
              <MessageCircle size={16} /> WhatsApp
            </span>
          </button>
          {isSuperadmin && (
            <button type="button" data-testid="admin-tab-audit" onClick={() => cambiaTab("audit")} className={tabCls("audit")}>
              <span className="inline-flex items-center gap-2">
                <ScrollText size={16} /> Registro attività
              </span>
            </button>
          )}
          {isSuperadmin && (
            <button type="button" data-testid="admin-tab-operatori" onClick={() => cambiaTab("operatori")} className={tabCls("operatori")}>
              <span className="inline-flex items-center gap-2">
                <MapPin size={16} /> Operatori in zona
              </span>
            </button>
          )}
        </div>

        {tab === "richieste" && (
          <div className="mt-8">
            <div className="mb-6 flex flex-wrap gap-2" data-testid="admin-requests-filter-status">
              {["", ...STATI].map((s) => (
                <button
                  key={s || "tutte"}
                  type="button"
                  data-testid={`filtro-${s ? s.toLowerCase().replace(/ /g, "-") : "tutte"}`}
                  onClick={() => cambiaFiltro(s)}
                  className={`rounded-full border px-4 py-2 text-xs font-semibold transition-colors ${
                    filtro === s
                      ? "border-amber-500 bg-amber-500/10 text-amber-400"
                      : "border-slate-700 text-slate-400 hover:text-white"
                  }`}
                >
                  {s || "Tutte"}
                </button>
              ))}
            </div>

            {richieste.length === 0 ? (
              <p className="rounded-2xl border border-dashed border-slate-700 p-10 text-center text-sm text-slate-500" data-testid="richieste-vuote">
                Nessuna richiesta al momento. Le nuove richieste di assistenza appariranno qui.
              </p>
            ) : (
              <div className="space-y-4">
                {richieste.map((r) => (
                  <article
                    key={r.id}
                    data-testid={`richiesta-row-${r.id}`}
                    className="rounded-2xl border border-slate-800 bg-slatecard p-6"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <h3 className="font-display text-lg font-semibold text-white">{r.nome}</h3>
                        <a href={`tel:${r.telefono}`} className="mt-1 inline-flex items-center gap-1.5 text-sm text-amber-400 hover:text-amber-300">
                          <Phone size={13} /> {r.telefono}
                        </a>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${COLORE_STATO[r.stato]}`}>
                          {r.stato}
                        </span>
                        <select
                          data-testid={`richiesta-stato-select-${r.id}`}
                          value={r.stato}
                          onChange={(e) => cambiaStato(r.id, e.target.value)}
                          className="rounded-lg border border-slate-700 bg-obsidian px-3 py-2 text-xs text-slate-200 outline-none focus:border-amber-500"
                          aria-label="Cambia stato richiesta"
                        >
                          {STATI.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-400">
                      <span>
                        Servizio: <span className="text-slate-200">{r.servizio}</span>
                      </span>
                      <span>
                        Città: <span className="text-slate-200">{r.citta}</span>
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <Clock size={12} /> {formattaData(r.created_at)}
                      </span>
                    </div>
                    {r.messaggio && (
                      <p className="mt-3 rounded-lg bg-obsidian/60 p-4 text-sm leading-relaxed text-slate-300">
                        {r.messaggio}
                      </p>
                    )}
                    <div className="mt-4 border-t border-slate-800 pt-4">
                      <WhatsAppGira
                        contesto={`richiesta-${r.id}`}
                        testo={`Nuovo intervento — ${r.servizio} a ${r.citta}. Cliente: ${r.nome}, tel ${r.telefono}.${r.messaggio ? ` Note: ${r.messaggio}` : ""}`}
                      />
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "chat" && (
          <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-[320px_1fr]">
            <div className="space-y-3">
              {conversazioni.length === 0 ? (
                <p className="rounded-2xl border border-dashed border-slate-700 p-8 text-center text-sm text-slate-500" data-testid="conversazioni-vuote">
                  Nessuna conversazione. Quando un visitatore apre la chat, la trovi qui.
                </p>
              ) : (
                conversazioni.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    data-testid={`conversazione-item-${c.id}`}
                    onClick={() => apriConversazione(c)}
                    className={`w-full rounded-xl border p-4 text-left transition-colors ${
                      attiva?.id === c.id
                        ? "border-amber-500/60 bg-amber-500/5"
                        : "border-slate-800 bg-slatecard hover:border-slate-600"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold text-white">{c.nome}</p>
                      <span
                        className={`h-2 w-2 rounded-full ${
                          c.stato === "Aperta" ? "bg-emerald-400" : "bg-slate-600"
                        }`}
                        title={c.stato}
                      />
                    </div>
                    <p className="mt-1 truncate text-xs text-slate-500">
                      {c.messaggi.length > 0
                        ? c.messaggi[c.messaggi.length - 1].testo
                        : "Nessun messaggio ancora"}
                    </p>
                    <p className="mt-1.5 text-[10px] text-slate-600">
                      {c.messaggi.length} messaggi · {formattaData(c.ultimo_messaggio_at)}
                    </p>
                  </button>
                ))
              )}
            </div>

            <div className="glass-panel flex min-h-[440px] flex-col rounded-2xl">
              {!attiva ? (
                <div className="flex flex-1 items-center justify-center p-10 text-sm text-slate-500" data-testid="chat-nessuna-selezionata">
                  Seleziona una conversazione per leggere i messaggi e rispondere.
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between border-b border-slate-800 px-6 py-4">
                    <div>
                      <p className="font-display text-base font-semibold text-white">{attiva.nome}</p>
                      <p className="text-xs text-slate-500">
                        {attiva.telefono || "Telefono non fornito"} · Stato: {attiva.stato}
                      </p>
                    </div>
                    <button
                      type="button"
                      data-testid="admin-chat-toggle-stato-btn"
                      onClick={toggleStatoChat}
                      className="rounded-full border border-slate-700 px-4 py-2 text-xs text-slate-300 transition-colors hover:border-amber-500/50 hover:text-amber-400"
                    >
                      {attiva.stato === "Aperta" ? "Chiudi conversazione" : "Riapri conversazione"}
                    </button>
                  </div>
                  <div className="border-b border-slate-800 px-6 py-3">
                    <WhatsAppGira
                      contesto={`chat-${attiva.id}`}
                      testo={`Chat dal sito — ${attiva.nome}${attiva.telefono ? ` (${attiva.telefono})` : ""}. Ultimo messaggio: ${attiva.messaggi.length > 0 ? attiva.messaggi[attiva.messaggi.length - 1].testo : "nessun messaggio"}`}
                    />
                  </div>
                  <div className="flex-1 space-y-3 overflow-y-auto p-6" data-testid="admin-chat-messaggi">
                    {attiva.messaggi.length === 0 && (
                      <p className="text-center text-xs text-slate-500">
                        Il visitatore non ha ancora scritto. Rispondi tu per primo.
                      </p>
                    )}
                    {attiva.messaggi.map((m, i) => (
                      <div key={i} className={`flex ${m.mittente === "admin" ? "justify-end" : "justify-start"}`}>
                        <div
                          className={`max-w-[75%] rounded-2xl px-4 py-3 text-sm ${
                            m.mittente === "admin"
                              ? "rounded-tr-sm bg-amber-500 text-obsidian"
                              : "rounded-tl-sm bg-slate-800 text-slate-200"
                          }`}
                        >
                          <p>{m.testo}</p>
                          <p className={`mt-1 text-[10px] ${m.mittente === "admin" ? "text-obsidian/60" : "text-slate-500"}`}>
                            {formattaOra(m.created_at)}
                          </p>
                        </div>
                      </div>
                    ))}
                    <div ref={fondoRef} />
                  </div>
                  <form onSubmit={rispondi} className="flex items-center gap-2 border-t border-slate-800 p-4">
                    <input
                      data-testid="admin-chat-reply-input"
                      value={risposta}
                      onChange={(e) => setRisposta(e.target.value)}
                      placeholder="Scrivi una risposta…"
                      className="w-full rounded-xl border border-slate-700 bg-obsidian/70 px-4 py-3 text-sm text-white placeholder:text-slate-500 outline-none focus:border-amber-500"
                    />
                    <button
                      type="submit"
                      data-testid="admin-chat-send-btn"
                      aria-label="Invia risposta"
                      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-amber-500 text-obsidian transition-colors hover:bg-amber-400"
                    >
                      <Send size={17} />
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        )}

        {tab === "whatsapp" && (
          <div className="mt-8" data-testid="whatsapp-sezione">
            <WhatsAppPanel />
          </div>
        )}

        {tab === "audit" && isSuperadmin && (
          <div className="mt-8" data-testid="audit-sezione">
            <p className="mb-6 rounded-2xl border border-amber-500/25 bg-amber-500/5 p-5 text-sm leading-relaxed text-slate-300">
              Registro completo delle attività nella dashboard: accessi con luogo e IP di
              connessione, schede aperte, chat lette, risposte inviate, cambi di stato e
              sessioni attive, con data e ora. Visibile solo al superadmin.
            </p>
            {audit.length === 0 ? (
              <p className="rounded-2xl border border-dashed border-slate-700 p-10 text-center text-sm text-slate-500" data-testid="audit-vuoto">
                Nessuna attività registrata finora.
              </p>
            ) : (
              <div className="space-y-3">
                {audit.map((a, i) => (
                  <div
                    key={i}
                    data-testid={`audit-row-${i}`}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-800 bg-slatecard px-5 py-4"
                  >
                    <div className="flex items-center gap-4">
                      <span className="rounded-lg bg-amber-500/10 p-2 text-amber-400">
                        <ScrollText size={16} />
                      </span>
                      <div>
                        <p className="text-sm font-semibold text-white">{a.azione}</p>
                        <p className="text-xs text-slate-400">
                          {a.utente}
                          {a.dettaglio ? ` · ${a.dettaglio}` : ""}
                          {a.ip ? ` · IP ${a.ip}` : ""}
                        </p>
                      </div>
                    </div>
                    <span className="font-mono text-xs text-slate-500">
                      {formattaData(a.created_at)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "operatori" && isSuperadmin && (
          <div className="mt-8" data-testid="operatori-sezione">
            <p className="mb-6 rounded-2xl border border-emerald-500/25 bg-emerald-500/5 p-5 text-sm leading-relaxed text-slate-300">
              Posizioni condivise volontariamente dagli operatori con il pulsante
              "Condividi posizione" nella loro dashboard. La condivisione è sempre visibile
              all'operatore e può essere disattivata in qualsiasi momento.
            </p>
            {posizioni.length === 0 ? (
              <p className="rounded-2xl border border-dashed border-slate-700 p-10 text-center text-sm text-slate-500" data-testid="operatori-vuoto">
                Nessun operatore sta condividendo la posizione al momento.
              </p>
            ) : (
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                {posizioni.map((p, i) => {
                  const online =
                    p.attiva && Date.now() - new Date(p.updated_at).getTime() < 5 * 60 * 1000;
                  return (
                    <div
                      key={p.email}
                      data-testid={`operatore-card-${i}`}
                      className="rounded-2xl border border-slate-800 bg-slatecard p-5"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-white">{p.nome}</p>
                          <p className="text-xs text-slate-400">{p.email}</p>
                        </div>
                        <span
                          data-testid={`operatore-stato-${i}`}
                          className={`rounded-full border px-3 py-1 text-xs font-semibold ${
                            online
                              ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400"
                              : "border-slate-600 bg-slate-800 text-slate-400"
                          }`}
                        >
                          {online ? "In linea ora" : p.attiva ? "Condivisione attiva" : "Condivisione non attiva"}
                        </span>
                      </div>
                      <p className="mt-3 font-mono text-xs text-slate-500">
                        {Number(p.lat).toFixed(5)}, {Number(p.lng).toFixed(5)} · ultimo
                        aggiornamento {formattaData(p.updated_at)}
                      </p>
                      <iframe
                        title={`Mappa di ${p.nome}`}
                        data-testid={`operatore-mappa-${i}`}
                        src={`https://www.openstreetmap.org/export/embed.html?bbox=${p.lng - 0.01}%2C${p.lat - 0.01}%2C${p.lng + 0.01}%2C${p.lat + 0.01}&layer=mapnik&marker=${p.lat}%2C${p.lng}`}
                        className="mt-4 h-52 w-full rounded-xl border border-slate-700"
                        loading="lazy"
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
